import { t as uiText } from '../i18n';
// src/components/NotePreviewCard.tsx
import React from 'react';
import PreviewMetaBadges from './PreviewMetaBadges';
import { PreviewMeta } from '../types/preview';
import { toRealDate } from '../lib/realDate';
import newpageIcon from '../assets/image/icon/newpage.png';

interface NotePreviewCardProps {
  id?: string;
  title?: string;
  content?: string;
  firstImageUrl?: string | null;
  lastModified?: any;
  slug?: string;
  pinned?: boolean;
  archived?: boolean;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  isMobile?: boolean;
  onCardClick?: (slug: string) => void;
  onSelectionChange?: (id: string) => void;
  onPinToggle?: (id: string, pinned: boolean) => void;
  showPinButton?: boolean;
  showNewCardButton?: boolean;
  onClick?: () => void;
  hashtags?: string[]; // タグ情報を追加
  previewData?: any; // プレビューデータを追加
  publicPageStatus?: 'nonPublic' | 'standby' | 'published';
  /** 知識軸: AIの回答に使うか */
  publicRagEnabled?: boolean;
  /** ページ軸: Webページとして公開する意図があるか */
  publicWebIntended?: boolean;
  /** ワークスペースの「ページをWeb公開する」。OFF のあいだはページ軸のチップを出さない */
  webPagesEnabled?: boolean;
  enablePublicVisuals?: boolean;
  isCrawled?: boolean; // クローラーで取得されたページかどうか
  onCrawlerClick?: () => void; // クローラーページクリック時のコールバック
}

// Memo_で始まるパターンの判定関数
const isMemoPattern = (title: string): boolean => {
  return /^Memo_/.test(title.trim());
};

// 日時フォーマット関数
const formatLastModified = (lastModified: unknown): string => {
  const date = toRealDate(lastModified);
  if (!date) return '';
  return date.toLocaleDateString('ja-JP', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const NotePreviewCard: React.FC<NotePreviewCardProps> = ({
  id,
  title,
  content,
  firstImageUrl,
  lastModified,
  slug,
  pinned = false,
  archived = false,
  isSelectionMode = false,
  isSelected = false,
  isMobile = false,
  onCardClick,
  onSelectionChange,
  onPinToggle,
  showPinButton = false,
  showNewCardButton = false,
  onClick,
  hashtags = [], // タグ情報を追加
  previewData, // プレビューデータを追加
  publicPageStatus,
  publicRagEnabled,
  publicWebIntended,
  webPagesEnabled = true,
  enablePublicVisuals = false,
  isCrawled = false,
  onCrawlerClick
}) => {
  // 2軸チップ。旧データ（publicRagEnabled 未指定）は publicPageStatus から復元する
  const ragEnabled = publicRagEnabled ?? publicPageStatus !== 'nonPublic';
  const webIntended = webPagesEnabled && (publicWebIntended ?? publicPageStatus === 'published');
  const publicChipBaseStyle: React.CSSProperties = {
    padding: '2px 6px',
    fontSize: isMobile ? '9px' : '10px',
    fontWeight: 600,
    border: '1px solid',
    borderRadius: '12px',
    whiteSpace: 'nowrap',
  };
  const filledChipStyle: React.CSSProperties = {
    color: '#fff',
    backgroundColor: 'var(--workspace-primary, #cfae91)',
    borderColor: 'var(--workspace-primary, #cfae91)',
  };
  const outlinedChipStyle: React.CSSProperties = {
    color: 'var(--workspace-primary, #cfae91)',
    backgroundColor: '#fff',
    borderColor: 'var(--workspace-primary, #cfae91)',
  };
  const mutedChipStyle: React.CSSProperties = {
    color: '#fff',
    backgroundColor: '#777',
    borderColor: '#777',
  };

  // プレビューデータからフォールバック値を取得
  const displayTitle = title || previewData?.title || previewData?.structuredData?.headline;
  const memoHidden = !!displayTitle && isMemoPattern(displayTitle);
  const pinButtonTitle = pinned ? uiText("Unpin") : uiText("Pin");
  
  // クリーンなプレビューテキストの取得
  const getCleanPreviewText = (previewData: any): string => {
    // 新しいpreviewMetaがある場合はそれを優先使用
    if (previewData?.previewMeta?.previewText) {
      return previewData.previewMeta.previewText;
    }
    
    // フォールバック：従来の複雑な抽出処理
    if (!previewData?.content) return '';
    
    try {
      if (Array.isArray(previewData.content)) {
        // TipTap形式のコンテンツから純粋なテキストのみを抽出
        const extractTextFromNode = (node: any): string => {
          if (!node?.content || !Array.isArray(node.content)) return '';

          const parts: string[] = node.content
            .map((item: any) => {
              if (item.type === 'text' && item.text) return item.text;
              // ネストしたコンテンツを再帰的に処理（画像やファイル添付は除外）
              if (item.content) return extractTextFromNode(item);
              return '';
            })
            .filter(Boolean);

          // 表は区切りを入れないと値の対応が読めなくなる
          if (node.type === 'table') return parts.join('\n');
          if (node.type === 'tableRow') return parts.join(' / ');

          return parts.join('');
        };
        
        const extractedTexts = previewData.content
          .map((node: any) => {
            if (node.type === 'paragraph' || node.type === 'table') {
              return extractTextFromNode(node);
            }
            return '';
          })
          .filter(Boolean);
        
        return extractedTexts.join(' ').trim();
      } else if (typeof previewData.content === 'string') {
        return previewData.content;
      }
    } catch (error) {
      console.warn('Content extraction error:', error);
    }
    
    return '';
  };
  
  const displayContent = (typeof content === 'string' ? content : '') || getCleanPreviewText(previewData) || '';
  
  // 画像の抽出 - previewDataからも取得
  const getFirstImageFromPreviewData = (previewData: any): string => {
    if (!previewData?.content || !Array.isArray(previewData.content)) return '';
    
    try {
      for (const node of previewData.content) {
        if (node.content && Array.isArray(node.content)) {
          for (const item of node.content) {
            if (item.type === 'image' && item.attrs?.src) {
              return item.attrs.src;
            }
          }
        }
      }
    } catch (error) {
      console.warn('Image extraction error:', error);
    }
    
    return '';
  };
  
  // 画像URLの取得（実際に存在する画像URLを優先）
  const displayFirstImageUrl = firstImageUrl || 
                               previewData?.thumbnailUrl || 
                               previewData?.structuredData?.thumbnailUrl ||
                               previewData?.previewMeta?.firstImageThumb ||
                               getFirstImageFromPreviewData(previewData);
                               
  // 新しいプレビューメタデータを取得
  const previewMeta: PreviewMeta | undefined = previewData?.previewMeta;

  // レンダリング後のDOM要素確認（開発環境のみ）
  React.useEffect(() => {
    if (import.meta.env.MODE === 'development' && id) {
      const cardElement = document.querySelector(`[data-board-id="${id}"]`) as HTMLElement;
    }
  }, [id, title]);
  const handleCardClick = () => {
    // クローラーページの場合は専用のコールバックを実行
    if (isCrawled && onCrawlerClick) {
      onCrawlerClick();
      return;
    }
    
    // クローラーページは選択モードで選択不可
    if (isSelectionMode && id && !isCrawled) {
      onSelectionChange?.(id);
    } else if (isSelectionMode && isCrawled) {
      // クローラーページの場合は選択モードでも通常クリック動作
      if (onCardClick && slug) {
        onCardClick(slug);
      } else if (onClick) {
        onClick();
      }
    } else if (onCardClick && slug) {
      onCardClick(slug);
    } else if (onClick) {
      onClick();
    }
  };

  const handlePinToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id) {
      onPinToggle?.(id, pinned);
    }
  };

  const handleSelectionChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id) {
      onSelectionChange?.(id);
    }
  };

  // 新規作成カードの場合
  if (showNewCardButton || (!id && !displayTitle && !displayContent)) {
    return (
      <div
        style={{
          border: '2px dashed #ccc',
          borderRadius: '8px',
          width: isMobile ? 'calc(100% - 8px)' : '200px',
          height: isMobile ? '240px' : '282px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: '#fafafa',
          transition: 'background-color 0.3s',
          padding: isMobile ? '10px 12px' : '12px 12px',
          boxSizing: 'border-box',
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.backgroundColor = '#fafafa';
          }
        }}
      >
        <div
          style={{
            lineHeight: '1',
          }}
        >
          <img 
            src={newpageIcon} 
            alt={uiText("Create new")} 
            style={{ 
              width: isMobile ? '48px' : '64px', 
              height: isMobile ? '48px' : '64px',
              filter: 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)'
            }} 
          />
        </div>
        <div
          style={{
            fontSize: isMobile ? '14px' : '16px',
            color: '#666',
          }}
        >
          {uiText("Create new")}
        </div>
      </div>
    );
  }

  // 通常のノートカード
  return (
    <div
      data-testid="board-card"
      data-board-id={id}
      style={{
        // 公開状態に応じた外枠（standby は薄いベージュ、published は濃いベージュ）
        border: (enablePublicVisuals && publicPageStatus === 'standby')
          ? '1px solid var(--workspace-border-soft, #f5f0eb)'
          : (enablePublicVisuals && publicPageStatus === 'published' || pinned)
          ? '1px solid var(--workspace-primary, #cfae91)'
          : '1px solid #e0e0e0',
        borderRadius: '8px',
        width: isMobile ? 'calc(100% - 8px)' : '200px',
        height: isMobile ? '240px' : '282px',
        display: 'flex',
        flexDirection: 'column',
        cursor: isSelectionMode && !isCrawled ? 'default' : 'pointer',
        backgroundColor: isSelectionMode && isSelected && !isCrawled
          ? '#f8f4f0' 
          : '#fff',
        transition: 'all 0.3s',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        padding: isMobile ? '10px 12px' : '12px 12px',
        boxSizing: 'border-box',
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        if ((!isSelectionMode || isCrawled) && !isMobile) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          // ピンボタンを表示
          const pinButton = e.currentTarget.querySelector('[data-pin-button]') as HTMLElement;
          if (pinButton) {
            pinButton.style.opacity = '1';
          }
        }
      }}
      onMouseLeave={(e) => {
        if ((!isSelectionMode || isCrawled) && !isMobile) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          // ピンボタンを非表示
          const pinButton = e.currentTarget.querySelector('[data-pin-button]') as HTMLElement;
          if (pinButton) {
            pinButton.style.opacity = '0';
          }
        }
      }}
    >
      {/* クローラーチップ表示（最優先） */}
      {isCrawled ? (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            left: '-4px',
            padding: '2px 6px',
            fontSize: isMobile ? '9px' : '10px',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: 'var(--workspace-primary, #cfae91)',
            border: '1px solid var(--workspace-primary, #cfae91)',
            borderRadius: '12px',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {uiText("Crawler")}
        </div>
      ) : (
        /* 公開ステータス表示（クローラーでない場合のみ）。知識軸とページ軸を別チップで見せる */
        enablePublicVisuals && publicPageStatus && (
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              left: '-4px',
              display: 'flex',
              gap: '4px',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {ragEnabled ? (
              <>
                <span style={{ ...publicChipBaseStyle, ...filledChipStyle }}>{uiText("AI answer")}</span>
                {webIntended && (
                  <span style={{ ...publicChipBaseStyle, ...outlinedChipStyle }}>
                    {publicPageStatus === 'published' ? uiText("Web publishing") : uiText("Generating web page")}
                  </span>
                )}
              </>
            ) : (
              <span style={{ ...publicChipBaseStyle, ...mutedChipStyle }}>{uiText("Private")}</span>
            )}
          </div>
        )
      )}
      {/* 選択モード時のチェックボックス（クローラーページは選択不可） */}
      {isSelectionMode && !isCrawled && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '20px',
            height: '20px',
            border: '2px solid var(--workspace-primary, #cfae91)',
            borderRadius: '4px',
            backgroundColor: isSelected ? 'var(--workspace-primary, #cfae91)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1,
          }}
          onClick={handleSelectionChange}
        >
          {isSelected && (
            <div style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</div>
          )}
        </div>
      )}
      {/* ピンボタン */}
      {!isSelectionMode && showPinButton && (
        <div
          data-pin-button
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '24px',
            height: '24px',
            backgroundColor: 'var(--workspace-primary, #cfae91)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1,
            color: 'white',
            fontSize: '12px',
            opacity: 0,
            transition: 'opacity 0.3s',
          }}
          onClick={handlePinToggle}
          title={pinButtonTitle}
        >
          📌
        </div>
      )}
      {/* タイトル（Memo_数字パターンの場合は非表示） */}
      {displayTitle && !memoHidden && (
        <div
          style={{
            marginBottom: '4px',
            flex: '0 0 auto',
          }}
        >
          <div
            style={{
              fontSize: isMobile ? '14px' : '14px',
              fontWeight: 'bold',
              color: '#333',
              lineHeight: '1.3',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {displayTitle}
          </div>
        </div>
      )}
      {/* 画像サムネイル */}
      {displayFirstImageUrl && (
        <div
          style={{
            marginBottom: '4px',
            flex: '0 0 auto',
            marginLeft: isMobile ? '-6px' : '-8px',
            marginRight: isMobile ? '-6px' : '-8px',
          }}
        >
          <img
            src={displayFirstImageUrl}
            alt={uiText("Thumbnail")}
            style={{
              width: '100%',
              height: isMobile ? '86.9px' : '97.4px',
              aspectRatio: '1280 / 670',
              objectFit: 'cover',
              borderRadius: '8px',
              border: 'none',
              opacity: 1,
              transition: 'opacity 0.3s ease',
            }}
            onError={(e) => {
              // 画像読み込みエラー時は透明化（レイアウト崩れを防ぐ）
              e.currentTarget.style.opacity = '0';
              
              // 親要素も非表示にしてスペースを削除
              const parentDiv = e.currentTarget.parentElement;
              if (parentDiv) {
                parentDiv.style.display = 'none';
              }
            }}
            onLoad={(e) => {
              // 画像読み込み成功時は不透明度を元に戻す
              e.currentTarget.style.opacity = '1';
            }}
          />
        </div>
      )}
      {/* コンテンツ */}
      <div
        style={{
          flex: '1',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            fontSize: isMobile ? '12px' : '12px',
            color: '#666',
            lineHeight: '1.4',
            wordBreak: 'break-word',
            whiteSpace: 'pre-line',
            overflow: 'hidden',
          }}
        >
          {(() => {
            // displayContentを使用し、型安全にする
            let finalDisplayContent = displayContent || uiText("No content");
            
            // 文字列型チェックを追加
            if (typeof finalDisplayContent === 'string') {
              // 応急措置：同じ文字列が連続している場合は改行で区切る
              if (!finalDisplayContent.includes('\n') && !finalDisplayContent.includes('📎')) {
                const words = finalDisplayContent.split(' ');
                if (words.length > 1 && words.every(word => word === words[0] && word.trim() !== '')) {
                  finalDisplayContent = words.join('\n');
                }
              }
            } else {
              // 文字列でない場合のフォールバック
              finalDisplayContent = uiText("No content");
            }
            
            return finalDisplayContent;
          })()}
        </div>
      </div>
      {/* ハッシュタグ表示 */}
      {hashtags && hashtags.length > 0 && (
        <div
          style={{
            marginTop: '8px',
            flex: '0 0 auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
              overflow: 'hidden', // はみ出し部分を非表示
              width: '100%', // 親要素の幅に制限
            }}
            className="hashtag-scroll-container"
          >
            {hashtags.map((tag, index) => (
              <span
                key={index}
                style={{
                  fontSize: isMobile ? '9px' : '10px',
                  color: '#fff',
                  backgroundColor: 'var(--workspace-primary, #cfae91)',
                  padding: '2px 6px',
                  borderRadius: '12px',
                  border: '1px solid var(--workspace-primary, #cfae91)',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.2',
                  flexShrink: 1, // タグの縮小を許可
                  minWidth: 0, // flexアイテムの最小幅を0に設定
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* プレビューメタバッジ */}
      <PreviewMetaBadges 
        previewMeta={previewMeta}
        isMobile={isMobile}
        showImages={true}
        showAttachments={true}
        showInternalLinks={true}
        showExternalLinks={true}
        combineLinkTypes={false}
      />
      
      {/* 最終編集日時。未設定や epoch は出さない */}
      {formatLastModified(lastModified) && (
      <div
        style={{
          marginTop: '8px',
          flex: '0 0 auto',
        }}
      >
        <div
          style={{
            fontSize: isMobile ? '9px' : '10px',
            color: '#999',
            lineHeight: '1.2',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span>
            {formatLastModified(lastModified)}
          </span>
          <span>{uiText("Update")}</span>
        </div>
      </div>
      )}
    </div>
  );
};

export default NotePreviewCard;
